/** @jsx jsx */
import {
  Fragment,
  useState,
  FormEvent,
  useEffect,
  useMemo,
  useCallback,
} from 'react'
import {toast} from 'react-toastify'
import {jsx} from 'theme-ui'
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3'
import PaymentForm from './PaymentForm'
import DonationFooter from './DonationFooter'
import DonationMultiPriceBlock from './DonationMultiPriceBlock'
import DonationPurposeBlock from './DonationPurposeBlock'
import DonationSinglePriceBlock from './DonationSinglePriceBlock'
import DonationTypeBlock from './DonationTypeBlock'
import DonationUserInfo from './DonationUserInfo'
import {
  DISABLE_AMOUNTS_FOR,
  DonationPurpose,
  DonationType,
  getAmounts,
  UserInfo,
  AllDonationType,
  WaterForAllDonationType,
  PaymentMethod,
  Metadata,
  RamadanBasketDonationType,
  RamadanIftarDonationType,
  AtlasBasketDonationType,
  UdhiyaDonationType,
  NoorBasketDonationType,
  IftarSuhoorDonationType,
  MINIMAL_DONATION_AMOUNT,
  DonationPurposeSlugs,
  DEFAULT_DONATION_PURPOSE,
  getDonationTypes,
  DISABLED_DONATION_PURPOSES,
} from './shared'
import useStripePayment from 'hooks/useStripePayment'
import BuildYourOwnWell from './BuildYourOwnWell'
import usePayPalPayment from 'hooks/usePayPalPayment'
import FixedLoader from 'components/FixedLoader'
import ThankYouBox from './ThankYouBox'
import ErrorBox from './ErrorBox'
import FamilyBasket from './FamilyBasket'
import {useTranslation} from 'react-i18next'
import {Locale} from 'i18n'
import Udhiya from './Udhiya'

type PageNumbers = 0 | 1 | 2 | 3

interface IDonationBox {
  project: DonationPurpose
}

const DonationBox: React.FC<IDonationBox> = ({project}) => {
  const {t, i18n} = useTranslation()
  const {executeRecaptcha} = useGoogleReCaptcha()
  const language = i18n.language as Locale
  const [pageNumber, setPageNumber] = useState<PageNumbers>(0)
  const [donationPurpose, setDonationPurpose] = useState<DonationPurpose>(
    project || DEFAULT_DONATION_PURPOSE,
  )
  const [prevDonationPurpose, setPrevDonationPurpose] = useState(
    donationPurpose,
  )
  const donationTypes = useMemo(() => getDonationTypes(donationPurpose), [
    donationPurpose,
  ])
  const defaultDonationType = useMemo(() => {
    if (donationPurpose === DonationPurpose['Atlas Relief']) {
      return AllDonationType['Atlas Basket']
    } else if (donationPurpose === DonationPurpose['Water For All']) {
      return AllDonationType['Build Your Own Well']
    } else if (
      donationPurpose === DonationPurpose['Ansar'] ||
      donationPurpose === DonationPurpose['General Donation']
    ) {
      return AllDonationType['Monthly']
    } else if (donationTypes.includes(DonationType['One Time'])) {
      return AllDonationType['One Time']
    } else {
      return donationTypes[0] as AllDonationType
    }
  }, [donationPurpose, donationTypes])
  const [donationType, setDonationType] = useState<AllDonationType>(
    defaultDonationType,
  )
  const hasAnsarSupport = useMemo(() => {
    return (
      donationType !== DonationType['Monthly'] &&
      donationPurpose !== DonationPurpose['Ansar']
    )
  }, [donationType, donationPurpose])

  const [option, setOption] = useState<number>(-1)
  const [metadata, setMetadata] = useState<Metadata | null>(null)
  const [ansar, setAnsar] = useState<boolean>(true)
  const [amount, setAmount] = useState<number | null>(null)
  const [userInput, setUserInput] = useState<'custom' | 'fixed' | null>(null)
  const ansarAmount = useMemo<number>(() => {
    if (amount === null) return 0
    return amount > 0 && amount <= 50
      ? 5
      : amount > 50 && amount < 100
      ? 10
      : (amount * 7) / 100
  }, [amount])
  const handleChangeDonationPurpose = (newDonationPurpose: DonationPurpose) => {
    // setAmount(null)
    setMetadata(null)
    setDonationPurpose(newDonationPurpose)
  }
  const [userInfo, setUserInfo] = useState<UserInfo>({
    firstName: '',
    lastName: '',
    email: '',
  })
  useEffect(() => {
    if (donationPurpose !== prevDonationPurpose) {
      setDonationType(defaultDonationType)
    }
    setPrevDonationPurpose(donationPurpose)
  }, [
    donationPurpose,
    setDonationType,
    defaultDonationType,
    prevDonationPurpose,
  ])
  useEffect(() => {
    const slug = Object.keys(DonationPurposeSlugs).find(
      (key) =>
        DonationPurposeSlugs[key] === donationPurpose &&
        !DISABLED_DONATION_PURPOSES.includes(DonationPurposeSlugs[key]),
    )
    window.history.replaceState(
      {},
      '',
      language === 'en' ? `/${slug}` : `/${language}/${slug}`,
    )
  }, [donationPurpose, language])
  useEffect(() => {
    if (metadata !== null && donationType in DonationType) {
      // We are in a normal donation type, we don't need metadata
      setMetadata(null)
    }
  }, [donationType, metadata])
  const handlePaymentCompleted = (success: boolean) => {
    if (success) setPageNumber(2)
    else setPageNumber(3)
  }
  const showAmounts = !DISABLE_AMOUNTS_FOR.includes(donationPurpose)
  const isMonthlyDonation = donationType === DonationType.Monthly
  const footerNotes = isMonthlyDonation
    ? [t('donationBoxNotice'), t('donationBoxTaxNotice')]
    : [t('donationBoxTaxNotice')]
  const amounts: number[] =
    donationType in AllDonationType ? getAmounts(donationType) : []
  const [paymentMethod, setPaymentMethod] = useState<
    PaymentMethod['methodName']
  >('stripe')
  const [payerName, setPayerName] = useState<string>('')
  const {
    initiatePayment: initiateStripePayment,
    loading: stripeLoading,
  } = useStripePayment()
  const {
    initiatePayment: initiatePayPalPayment,
    loading: payPalLoading,
  } = usePayPalPayment({
    onPaymentCompleted: handlePaymentCompleted,
  })
  const [areStripeElementsComplete, setStripeElementsComplete] = useState({
    cardNumber: false,
    cardExpiry: false,
    cardCvc: false,
  })

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const recaptcha = (await executeRecaptcha?.('homepage')) || ''
    const {firstName, lastName, email} = userInfo
    if (
      donationType === WaterForAllDonationType['Build Your Own Well'] &&
      !metadata
    ) {
      toast.warn(t('You need to select the type of well'))
      return
    }
    if (!amount || amount < MINIMAL_DONATION_AMOUNT) {
      toast.warn(
        `You need to add an amount of at least $${MINIMAL_DONATION_AMOUNT} USD`,
      )
      return
    }
    // if (!email || !/\S+@\S+\.\S+/.test(email)) {
    //   toast.warn('You need to enter a valid email')
    //   setPageNumber(0)
    //   return
    // }
    // if (!firstName || !lastName) {
    //   toast.warn('You need to enter your first name and your last name')
    //   setPageNumber(0)
    //   return
    // }

    if (pageNumber === 0) {
      // Scroll to the top of the page
      window.scrollTo(0, 0)
      setPageNumber(1)
    } else {
      if (paymentMethod === 'stripe') {
        let valid = true
        Object.entries(areStripeElementsComplete).forEach(
          ([element, complete]) => {
            if (!complete) {
              // https://stackoverflow.com/a/44280814/1673761
              const elementName = element
                .split(/(?=[A-Z])/)
                .join(' ')
                .toLowerCase()
              toast.error(`Your ${elementName} is incomplete`)
              valid = false
            }
          },
        )
        if (!valid) return
        const {success} = await initiateStripePayment({
          amount,
          firstName,
          lastName,
          email,
          donationPurpose,
          payerName,
          metadata,
          isRecurrent: donationType === DonationType.Monthly,
          language,
          recaptcha,
          ansar: hasAnsarSupport && ansar && !!ansarAmount,
        })
        handlePaymentCompleted(success)
        return
      } else if (paymentMethod === 'payPal') {
        initiatePayPalPayment({
          amount,
          firstName,
          lastName,
          email,
          donationPurpose,
          metadata,
          isRecurrent: donationType === DonationType.Monthly,
          language,
          recaptcha,
          ansar: hasAnsarSupport && ansar && !!ansarAmount,
        })
      } else {
        // never
        throw new Error('This payment method is not handled')
      }
    }
  }

  const handleBackClick = () => {
    if (pageNumber === 1 || pageNumber === 2) setPageNumber(0)
    if (pageNumber === 3) setPageNumber(1)
  }

  const handleAmountChange = useCallback(
    (newAmount: number | null, userInput: null | 'custom' | 'fixed') => {
      setAmount(newAmount)
      setUserInput(userInput)
    },
    [],
  )

  const isNormalDonation = donationType in DonationType
  return (
    <Fragment>
      <form
        onSubmit={handleSubmit}
        sx={{
          p: [28, , 24],
          backgroundColor: '#fff',
          borderRadius: [, , 19],
          width: ['100%', 387],
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {pageNumber === 0 && (
          <Fragment>
            <DonationPurposeBlock
              donationPurpose={donationPurpose}
              setDonationPurpose={handleChangeDonationPurpose}
            />
            <div
              sx={{
                mt: 24,
                backgroundColor: '#F5F5F5',
                borderRadius: 4,
              }}
            >
              <DonationTypeBlock
                donationPurpose={donationPurpose}
                donationType={donationType}
                setDonationType={setDonationType}
                donationTypes={donationTypes}
              />
              {isNormalDonation && showAmounts && (
                <DonationMultiPriceBlock
                  key={donationType + donationPurpose}
                  userInput={userInput}
                  amount={amount}
                  onAmountChange={handleAmountChange}
                  defaultAmount={amounts[1]}
                  priceTag={isMonthlyDonation ? 'USD/mo' : 'USD'}
                  amounts={amounts}
                />
              )}
              {donationType ===
                WaterForAllDonationType['Build Your Own Well'] && (
                <BuildYourOwnWell
                  option={option}
                  setOption={setOption}
                  onAmountChange={handleAmountChange}
                  metadata={metadata}
                  setMetadata={setMetadata}
                />
              )}
              {donationType === UdhiyaDonationType.Udhiya && (
                <Udhiya
                  onAmountChange={handleAmountChange}
                  setMetadata={setMetadata}
                />
              )}
              {donationType === RamadanBasketDonationType['Family Basket'] && (
                <FamilyBasket
                  amount={amount}
                  onAmountChange={handleAmountChange}
                  metadata={metadata}
                  setMetadata={setMetadata}
                  options={[
                    {
                      country: 'Others',
                      label: t('Iftar meal for a Family'),
                      price: 3,
                    },
                  ]}
                />
              )}
              {donationType === RamadanIftarDonationType['Iftar Meal'] && (
                <FamilyBasket
                  amount={amount}
                  onAmountChange={handleAmountChange}
                  metadata={metadata}
                  setMetadata={setMetadata}
                  options={[
                    {
                      country: 'Gaza',
                      label: t('Iftar meal for a Family in Gaza'),
                      price: 16,
                      disabled: true,
                    },
                    {
                      country: 'Others',
                      label: t('Iftar meal - Africa / Asia'),
                      price: 3,
                    },
                  ]}
                />
              )}
              {donationType === AtlasBasketDonationType['Atlas Basket'] && (
                <FamilyBasket
                  amount={amount}
                  onAmountChange={handleAmountChange}
                  metadata={metadata}
                  setMetadata={setMetadata}
                  options={[
                    {
                      country: 'Morocco',
                      label: t('Sponsor a family this winter'),
                      price: 100,
                    },
                  ]}
                />
              )}
              {donationType === NoorBasketDonationType['Noor Ummaty'] && (
                <FamilyBasket
                  amount={amount}
                  onAmountChange={handleAmountChange}
                  metadata={metadata}
                  setMetadata={setMetadata}
                  options={[
                    {
                      country: 'Morocco',
                      label: t('Eyes surgery (cataract)'),
                      price: 200,
                    },
                  ]}
                />
              )}
              {donationType ===
                IftarSuhoorDonationType['Iftar and Suhoor - New Muslims'] && (
                <FamilyBasket
                  amount={amount}
                  onAmountChange={handleAmountChange}
                  metadata={metadata}
                  setMetadata={setMetadata}
                  options={[
                    {
                      country: 'Others',
                      label: t('New Muslims'),
                      price: 5,
                    },
                  ]}
                />
              )}
            </div>
            {isNormalDonation && !showAmounts && (
              <DonationSinglePriceBlock
                key={donationType + donationPurpose}
                userInput={userInput}
                amount={amount}
                onAmountChange={handleAmountChange}
                priceTag="USD"
              />
            )}
            <DonationUserInfo userInfo={userInfo} setUserInfo={setUserInfo} />
            <DonationFooter notes={footerNotes} />
          </Fragment>
        )}
        {pageNumber === 1 && (
          <PaymentForm
            hasAnsarSupport={hasAnsarSupport}
            setStripeElementsComplete={setStripeElementsComplete}
            amount={amount || 0}
            isMonthlyDonation={isMonthlyDonation}
            ansarAmount={ansarAmount}
            donationPurpose={donationPurpose}
            handleBackClick={handleBackClick}
            payerName={payerName}
            setPayerName={setPayerName}
            ansar={ansar}
            setAnsar={setAnsar}
            isLoading={stripeLoading || payPalLoading}
            paymentMethod={paymentMethod}
            setPaymentMethod={setPaymentMethod}
          />
        )}
        {pageNumber === 2 && <ThankYouBox handleBackClick={handleBackClick} />}
        {pageNumber === 3 && <ErrorBox handleBackClick={handleBackClick} />}
        {/* <pre>
          {JSON.stringify(
            {
              pageNumber,
              donationPurpose,
              donationType,
              userInput,
              amount,
              userInfo,
              metadata,
              isMonthlyDonation,
              ansarAmount,
              hasAnsarSupport,
              paymentMethod,
              payerName,
              ansar,
            },
            null,
            2,
          )}
        </pre> */}
      </form>
      {(stripeLoading || payPalLoading) && <FixedLoader />}
    </Fragment>
  )
}

export default DonationBox
